import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  EditAmountDialogCloseResult,
  EditAmountService,
  EditServiceAmountScope,
  formatEditAmountErrorText,
  getEditAmountWarningToast,
  isEditAmountBlockedByOrder,
  isGroupedAmountScopeAllowed
} from 'src/entities/file';
import {
  AMOUNT_DECIMALS_ERROR,
  formatMoneyDisplay,
  restrictAmountText
} from 'src/shared/lib/money';
import Swal from 'sweetalert2';

export interface DialogEditTarifaData {
  serviceId: string;
  currentTotal?: number | null;
  tipoServicio?: string | null;
  moneda?: string | null;
  tipoCambio?: number | null;
  /** Nombre del servicio para contexto en el header. */
  nombreServicio?: string | null;
}

@Component({
  selector: 'app-dialog-edit-tarifa',
  templateUrl: './dialog-edit-tarifa.component.html',
  styleUrls: ['./dialog-edit-tarifa.component.css']
})
export class DialogEditTarifaComponent implements OnInit, OnDestroy {
  amount: number | null = null;
  scope: EditServiceAmountScope = 'SINGLE';
  loading = false;
  showNegativeError = false;
  amountError: string | null = null;
  saveError: string | null = null;

  readonly allowGrouped: boolean;
  readonly currencyLabel: string;
  readonly currencyPrefix: string;
  readonly tipoCambio: number | null;
  readonly serviceName: string | null;
  readonly currentTotalLabel: string | null;

  private saveSub?: Subscription;
  private usuario = '';
  private email = '';

  constructor(
    public dialogRef: MatDialogRef<
      DialogEditTarifaComponent,
      EditAmountDialogCloseResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: DialogEditTarifaData,
    private readonly editAmountService: EditAmountService
  ) {
    this.allowGrouped = isGroupedAmountScopeAllowed(data?.tipoServicio);
    this.currencyLabel = this.resolveCurrencyLabel(data?.moneda);
    this.currencyPrefix = this.currencyLabel === 'USD' ? '$' : 'S/';
    const tc = Number(data?.tipoCambio);
    this.tipoCambio = Number.isFinite(tc) && tc > 0 ? tc : null;
    this.serviceName = data?.nombreServicio?.trim() || null;

    const current = Number(data?.currentTotal);
    this.currentTotalLabel =
      Number.isFinite(current) && current > 0
        ? `${this.currencyPrefix} ${formatMoneyDisplay(current)}`
        : null;
  }

  ngOnInit(): void {
    const nombre = localStorage.getItem('nombre') ?? '';
    const apellido = localStorage.getItem('apellido') ?? '';
    this.usuario = `${nombre} ${apellido}`.trim();
    this.email = localStorage.getItem('email') ?? '';

    if (this.data?.currentTotal != null && this.data.currentTotal !== ('' as any)) {
      const current = Number(this.data.currentTotal);
      if (Number.isFinite(current) && current > 0) {
        this.amount = current;
      }
    }
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
  }

  get canSubmit(): boolean {
    return (
      !this.loading &&
      Boolean(this.data?.serviceId) &&
      Boolean(this.email) &&
      this.amount != null &&
      Number(this.amount) > 0 &&
      !this.showNegativeError
    );
  }

  onMontoInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.amountError = null;
    this.saveError = null;

    if (value.includes('-')) {
      this.showNegativeError = true;
      this.amount = null;
      return;
    }

    this.showNegativeError = false;
    if (value === '') {
      this.amount = null;
      return;
    }

    const restricted = restrictAmountText(value);
    if (restricted.exceeded) {
      this.amountError = AMOUNT_DECIMALS_ERROR;
      (event.target as HTMLInputElement).value = restricted.text;
    }
    const parsed = Number(restricted.text);
    this.amount = Number.isFinite(parsed) ? parsed : null;
  }

  onWheelPrevent(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  saveMonto(): void {
    if (this.loading) {
      return;
    }

    const amount = Number(this.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.amountError = 'Ingresa un monto mayor a 0';
      return;
    }
    if (restrictAmountText(String(this.amount)).exceeded) {
      this.amountError = AMOUNT_DECIMALS_ERROR;
      return;
    }

    if (!this.data?.serviceId) {
      this.saveError = 'Falta el identificador del servicio.';
      return;
    }

    if (!this.email) {
      this.saveError = 'Sesión sin email. Vuelve a iniciar sesión.';
      return;
    }

    if (this.tipoCambio != null && this.tipoCambio <= 0) {
      this.saveError = 'Falta tipo de cambio.';
      return;
    }

    const scope: EditServiceAmountScope =
      this.allowGrouped && this.scope === 'GROUPED' ? 'GROUPED' : 'SINGLE';

    this.loading = true;
    this.saveError = null;
    this.dialogRef.disableClose = true;

    this.saveSub?.unsubscribe();
    this.saveSub = this.editAmountService
      .editServiceAmount({
        serviceId: String(this.data.serviceId),
        amount,
        scope,
        createdBy: { name: this.usuario || this.email, email: this.email }
      })
      .subscribe({
        next: (result) => {
          this.loading = false;
          this.dialogRef.disableClose = false;

          if (!result?.ok) {
            this.saveError = 'No se pudo guardar el monto. Reintenta.';
            return;
          }

          this.showWarnings(result.warnings);
          this.dialogRef.close({ success: true, result });
        },
        error: (err) => {
          this.loading = false;
          this.dialogRef.disableClose = false;
          this.saveError = formatEditAmountErrorText(err);

          if (isEditAmountBlockedByOrder(err)) {
            Swal.fire({
              position: 'top-end',
              icon: 'error',
              title: this.saveError,
              showConfirmButton: false,
              timer: 4000
            });
            this.dialogRef.close({
              success: false,
              blockedByOrder: true
            });
          }
        }
      });
  }

  onCancel(): void {
    if (this.loading) {
      return;
    }
    this.dialogRef.close({ success: false });
  }

  private showWarnings(
    warnings: { code?: string | null; message?: string | null }[] | null | undefined
  ): void {
    if (!warnings?.length) {
      Swal.fire({
        position: 'top-end',
        icon: 'success',
        title: 'Monto actualizado',
        showConfirmButton: false,
        timer: 2500
      });
      return;
    }

    let showedSuccess = false;
    for (const warning of warnings) {
      const toast = getEditAmountWarningToast(warning);
      if (!toast) continue;

      if (warning.code === 'IDEMPOTENT_NO_CHANGE') {
        showedSuccess = true;
      }

      Swal.fire({
        position: 'top-end',
        icon: toast.icon,
        title: toast.title,
        text: toast.text,
        showConfirmButton: false,
        timer: 4000
      });
    }

    if (!showedSuccess) {
      Swal.fire({
        position: 'top-end',
        icon: 'success',
        title: 'Monto actualizado',
        showConfirmButton: false,
        timer: 2500
      });
    }
  }

  private resolveCurrencyLabel(moneda: string | null | undefined): string {
    const value = String(moneda ?? '')
      .toUpperCase()
      .trim();
    if (
      value === 'D' ||
      value === '$' ||
      value === 'USD' ||
      value === 'DOLAR' ||
      value === 'DOLARES'
    ) {
      return 'USD';
    }
    return 'Soles';
  }
}
